import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN'); // Formats as DD/MM/YYYY
};

function CareersList() {
  const [careerList, setCareerList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedCareer, setSelectedCareer] = useState(null);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const handleOpenModal = (career) => {
    setSelectedCareer(career);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedCareer(null);
  };

  const callApiCareerList = async () => {
    try {
      const headers = {
        "x-access-token": localStorage.getItem("jwttoken"),
      };
      const url = process.env.REACT_APP_API_URL + "career/alluser";
      const response = await axios.get(url, { headers });
      setCareerList(response.data);
      setFilteredList(response.data);
      setLoading(false);
    } catch (error) {
        console.log(error);

        setError(
          error.response?.data?.message ||
          "Failed to fetch career data"
        );

        setCareerList([]);
        setFilteredList([]);
        setLoading(false);
      }
  };

  useEffect(() => {
    callApiCareerList();
  }, []);

  // Search filter
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredList(careerList);
    } else {
      setFilteredList(
        careerList.filter((item) => {
          const nameMatch = item.name && item.name.toLowerCase().includes(searchTerm.toLowerCase());
          const emailMatch = item.email && item.email.toLowerCase().includes(searchTerm.toLowerCase());
          return nameMatch || emailMatch;
        })
      );
    }
    setCurrentPage(1); // Reset to first page on new search
  }, [searchTerm, careerList]);

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredList.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.max(
    1,
    Math.ceil(filteredList.length / itemsPerPage)
  );

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const goToNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const goToPrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));

  return (
    <div>
      <ToastContainer />

      {/* Main Card Container */}
      <div className="main-card">

        {/* Toolbar Section */}
        <div className="toolbar-container">
          <h2 className="page-title">Career Management</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span className="results-count">{filteredList.length} results</span>
            <input
              type="text"
              className="search-box"
              placeholder="Search by Name or Email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Gradient Header */}
        <div className="table-header-panel">
          Career List
        </div>

        {/* Table with both horizontal and vertical scrolling */}
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '70vh' }}>
          <table className="custom-table">
            <thead>
              <tr>
                <th>Sr No</th>
                <th>Name</th>
                <th>Mobile</th>
                <th>Referred By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5">
                    <div className="loading-container">
                      <div className="spinner"></div>
                    </div>
                  </td>
                </tr>
                ) : error ? (
                  <tr>
                    <td colSpan="5">
                      <div className="empty-state">
                        <div className="empty-state-icon">⚠️</div>
                        <div className="empty-state-text">
                          {error}
                        </div>
                      </div>
                    </td>
                  </tr>
              ) : filteredList.length === 0 ? (
                <tr>
                  <td colSpan="5">
                    <div className="empty-state">
                      <div className="empty-state-icon">📋</div>
                      <div className="empty-state-text">No careers found</div>
                    </div>
                  </td>
                </tr>
              ) : (
                currentItems.map((item, index) => (
                  <tr key={index + item.srno}>
                    <td>{item.srno}</td>
                    <td style={{ fontWeight: '600' }}>{item.name}</td>
                    <td>{item.mobile}</td>
                    <td>{item.referredby}</td>
                    <td>
                      <div className="action-btn-container">
                        <button
                          className="btn-action-edit"
                          style={{ borderColor: '#28a745', color: '#28a745', background: 'white' }}
                          onClick={() => handleOpenModal(item)}
                        >
                          View
                        </button>
                        <Link
                          to={`/careerupdate/${item.srno}`}
                          className="btn-action-edit"
                        >
                          Edit
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {filteredList.length > 0 && totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '15px', borderTop: '1px solid #eee', gap: '10px' }}>
            <button
              onClick={goToPrevPage}
              disabled={currentPage === 1}
              style={{
                padding: '6px 12px',
                border: '1px solid #ddd',
                backgroundColor: currentPage === 1 ? '#f8f9fa' : 'white',
                color: currentPage === 1 ? '#adb5bd' : '#333',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                borderRadius: '4px'
              }}
            >
              Previous
            </button>
            <span style={{ fontSize: '0.9rem', color: '#555' }}>
              Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
            </span>
            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              style={{
                padding: '6px 12px',
                border: '1px solid #ddd',
                backgroundColor: currentPage === totalPages ? '#f8f9fa' : 'white',
                color: currentPage === totalPages ? '#adb5bd' : '#333',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                borderRadius: '4px'
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Detail View Modal */}
      {showModal && selectedCareer && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header">
                <h5 className="modal-title">Career Details: {selectedCareer.name}</h5>
                <button type="button" className="btn-close" onClick={handleCloseModal}></button>
              </div>
              <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto', textAlign: 'left' }}>
                <div className="row g-3">
                  {Object.entries({
  "Sr No": selectedCareer.srno,
  "Reg Id": selectedCareer.regid,
  "Username": selectedCareer.username,
  "Password": selectedCareer.password,
  "Name": selectedCareer.name,
  "Email": selectedCareer.email,
  "Mobile": selectedCareer.mobile,
  "WhatsApp Mobile": selectedCareer.whatsapp_mobile,
  "City": selectedCareer.city,
  "Hometown": selectedCareer.hometown,
  "College": selectedCareer.college,
  "University": selectedCareer.university,
  "Passing Year": selectedCareer.passing_year,
  "Branch": selectedCareer.branch,
  "LinkedIn ID": selectedCareer.linkedinid, // Typo fixed: changed linkdinid to linkedinid
  "GitHub ID": selectedCareer.githubid,
  "Referred By": selectedCareer.referredby,
  "Current Company": selectedCareer.currentcompany,
  "Years of Exp": selectedCareer.yearsOfExperience,
  "Current Designation": selectedCareer.currentDesignation,
  "Notice Period": selectedCareer.noticePeriod,
  "Current Salary": selectedCareer.currentSalary,
  "Skills": selectedCareer.skills,
  "DOB": formatDate(selectedCareer.dob),
  "Work Location": selectedCareer.workLocation,
  "Type (Part/Full)": selectedCareer.typePartFull,
  "Type (Full/Intern)": selectedCareer.typeFullIntern,
  "Job Type": selectedCareer.jobType,
  "Create Date": formatDate(selectedCareer.createdon), // Formatted
  "Update Date": formatDate(selectedCareer.updatedon)  // Formatted
}).map(([label, value]) => (
  <div className="col-md-6" key={label}>
    <div className="detail-item p-2 border-bottom">
      <strong className="text-secondary small d-block">{label}</strong>
      {label === "LinkedIn ID" && value ? (
        <a href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noreferrer">Open Profile</a>
      ) : (
        <span className="text-dark">{value || 'N/A'}</span>
      )}
    </div>
  </div>
))}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary px-4" onClick={handleCloseModal}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CareersList;
